import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Centralized admin role check. Caches the result per-session in sessionStorage
 * to avoid repeated `user_roles` queries across components, while still being
 * authoritative against the database on first mount per session.
 *
 * Returns:
 *  - isAdmin: boolean | null (null until check completes)
 *  - loading: true while the role check is in flight
 *
 * IMPORTANT: This is for UI-gating only. RLS policies on the database tables
 * are the real security boundary — do not rely on this hook to enforce access.
 */
export function useIsAdmin(): { isAdmin: boolean; loading: boolean } {
  const { user } = useAuth();
  const [state, setState] = useState<{ isAdmin: boolean; loading: boolean }>(() => {
    // Optimistic read from sessionStorage
    if (typeof window !== "undefined" && user) {
      const cached = window.sessionStorage.getItem(`biletime.isAdmin.${user.id}`);
      if (cached === "true" || cached === "false") {
        return { isAdmin: cached === "true", loading: false };
      }
    }
    return { isAdmin: false, loading: !!user };
  });

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setState({ isAdmin: false, loading: false });
      return;
    }
    setState(s => ({ ...s, loading: true }));
    supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin")
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const isAdmin = !!data;
        try {
          window.sessionStorage.setItem(`biletime.isAdmin.${user.id}`, isAdmin ? "true" : "false");
        } catch { /* storage disabled */ }
        setState({ isAdmin, loading: false });
      });
    return () => { cancelled = true; };
  }, [user]);

  return state;
}
